import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { fmtDate } from '../../shared/Panel';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  cancelScheduledParameter,
  formatParameterValue,
  ownerText,
  saveParameterOwner,
  type SuggestionParameter,
} from './suggestionParametersApi';

/**
 * Okna właściciela parametru i odwołania zmiany zaplanowanej (prace
 * 24.10.2026, S-24.10-12, przełącznik `parameterOwnership`). Oba z
 * obowiązkowym powodem — zmiana trafia do historii parametru i dziennika
 * działań. Rozstrzyga API (uprawnienie sekcji administracyjnej).
 */

export function OwnerDialog({
  parameter,
  onClose,
  onSaved,
}: {
  parameter: SuggestionParameter;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [owner, setOwner] = useState(parameter.owner ?? '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (reason.trim() === '') {
      setError('Podaj powód zmiany');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const text = owner.trim();
      await saveParameterOwner(parameter.key, text === '' ? null : text, reason.trim());
      onSaved();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Właściciel — {parameter.label}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Właściciel biznesowy odpowiada za wartość parametru. Teraz:{' '}
          {ownerText(parameter.owner)}. Puste pole — właściciel nieustalony.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Właściciel"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            size="small"
            slotProps={{ htmlInput: { maxLength: 255, 'data-testid': 'parameter-owner-input' } }}
          />
          <TextField
            label="Powód zmiany"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            multiline
            minRows={2}
            required
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </Stack>
        {error && (
          <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button onClick={save} variant="contained" disabled={busy}>
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function CancelScheduleDialog({
  parameter,
  onClose,
  onSaved,
}: {
  parameter: SuggestionParameter;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const scheduled = parameter.scheduled;

  const save = async () => {
    if (reason.trim() === '') {
      setError('Podaj powód odwołania');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await cancelScheduledParameter(parameter.key, reason.trim());
      onSaved();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Odwołać zmianę zaplanowaną?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {parameter.label}:{' '}
          {scheduled
            ? `${formatParameterValue(scheduled.value, parameter)} od ${fmtDate(scheduled.validFrom)}`
            : '—'}
          . Zostaje obecna wartość {formatParameterValue(parameter.value, parameter)}.
        </Typography>
        <TextField
          label="Powód odwołania"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
          required
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
        {error && (
          <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button onClick={save} variant="contained" color="warning" disabled={busy}>
          Odwołaj zmianę
        </Button>
      </DialogActions>
    </Dialog>
  );
}
